"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { parse } from "date-fns";
import { fetchAndFormatEventData, handleCampaignID } from "@/lib/utils";
import {
  webinarSchedule,
  buildWebinarScheduleFromEvents,
  WebinarScheduleData,
} from "@/data/webinarSchedule";

type EventItem = {
  formattedDate: string;
  time: string;
  day: string;
  courseName: string;
  template: string;
  title: string;
};

const EventContext = createContext<EventItem[]>([]);

const WebinarScheduleContext =
  createContext<WebinarScheduleData>(webinarSchedule);

// Weekly/monthly webinar schedule (title/date/time), derived from the same
// `onlineEvents` fetch as `useEvent`. Falls back to the static defaults in
// `data/webinarSchedule.ts` until the fetch resolves (or if it fails / the
// API doesn't return enough events to map).
export const useWebinarSchedule = (): WebinarScheduleData =>
  useContext(WebinarScheduleContext);

// True only while the client fetch is still pending AND the server didn't
// supply a schedule (so `useWebinarSchedule` is showing the static fallback).
// Components render a shimmer in place of the date meanwhile so the wrong
// static date never flashes. Cleared once the fetch settles (even on failure).
const WebinarScheduleLoadingContext = createContext<boolean>(false);

export const useWebinarScheduleLoading = (): boolean =>
  useContext(WebinarScheduleLoadingContext);

export const useEvent = (course?: string): EventItem | null => {
  const events = useContext(EventContext);
  if (events.length === 0) return null;

  const today = new Date();

  const withDiff = events
    .filter((ev) => ev.formattedDate && ev.formattedDate !== "N/A")
    .map((ev) => ({
      ...ev,
      diff: Math.abs(
        parse(ev.formattedDate, "dd MMMM yyyy", new Date()).getTime() -
          today.getTime(),
      ),
    }));

  if (withDiff.length === 0) return null;

  const minDiff = Math.min(...withDiff.map((e) => e.diff));
  const closest = withDiff.filter((e) => e.diff === minDiff);

  if (course) {
    const normalised = course.toLowerCase();

    // Match among the closest group by courseName
    const matchedInClosest = closest.find(
      (e) => e.courseName.toLowerCase() === normalised,
    );
    if (matchedInClosest) return matchedInClosest;

    // No match in closest — fall back to the nearest USP event
    const uspFallback = withDiff
      .filter((e) => e.template === "USP")
      .sort((a, b) => a.diff - b.diff);
    if (uspFallback.length > 0) return uspFallback[0];
  }

  // No course specified or no USP fallback — return the overall closest
  return closest[0];
};

// `initialEvents` / `initialSchedule` come from the root layout's server fetch
// so the first (server-rendered) HTML already carries the real webinar date.
// The client fetch below still runs to refresh it.
export const EventProvider: React.FC<{
  children: React.ReactNode;
  initialEvents?: EventItem[];
  initialSchedule?: WebinarScheduleData | null;
}> = ({ children, initialEvents = [], initialSchedule }) => {
  const [events, setEvents] = useState<EventItem[]>(initialEvents);
  const [schedule, setSchedule] = useState<WebinarScheduleData>(
    initialSchedule ?? webinarSchedule,
  );
  const [isScheduleLoading, setIsScheduleLoading] =
    useState<boolean>(!initialSchedule);

  useEffect(() => {
    const init = async () => {
      try {
        handleCampaignID();
        const data = await fetchAndFormatEventData();
        setEvents(
          data.onlineEvents.map((event: any) => ({
            formattedDate: event.date,
            time: event.time,
            day: event.day,
            courseName: event.courseName,
            template: event.template,
            title: event.title,
          })),
        );

        const dynamicSchedule = buildWebinarScheduleFromEvents(
          data.onlineEvents,
        );
        if (dynamicSchedule) setSchedule(dynamicSchedule);
      } finally {
        setIsScheduleLoading(false);
      }
    };
    init();
  }, []);

  return (
    <EventContext.Provider value={events}>
      <WebinarScheduleContext.Provider value={schedule}>
        <WebinarScheduleLoadingContext.Provider value={isScheduleLoading}>
          {children}
        </WebinarScheduleLoadingContext.Provider>
      </WebinarScheduleContext.Provider>
    </EventContext.Provider>
  );
};
